import { Divider, Modal as AntModal, message } from "antd";
import {
  Loader,
  Modal,
  Select,
  Skeleton,
  Card,
  Text,
  Badge,
  Button,
  Stack,
  Collapse,
} from "@mantine/core";
import { useState, useEffect } from "react";
import LineItemsComponent from "./bill-items-component";
import {
  calculatePaystackAmount,
  cedisLocale,
  configurePaystack,
} from "../../helpers/utilities";

import { useMutation, useQuery, useQueryClient } from "react-query";
import _ from "lodash";
import {
  getAllNetworkPatients,
  getAllServices,
  getBill,
  putBill,
  getAllCharges,
  // putConvertBillToDraft removed: bills are edited in place without draft conversion
} from "../../helpers/api";

import { nanoid } from "nanoid";
import EditPayments from "./edit-payment";
import EditCharges from "./edit-charges";
import { useAtom } from "jotai";
import {
  billPaymentsAtom,
  setBillPayments,
  billChargesAtom,
  setBillCharges,
  billRedeemedBenefitsAtom,
  deleteBillChargeAtom,
  updateBillChargeAtom,
} from "../../helpers/state/edit-bill";
import smallTalk from "smalltalk";
import {
  IconPrinter,
  IconUser,
  IconReceipt,
  IconCreditCard,
  IconEye,
  IconEyeOff,
  IconNotes,
  IconDiscount,
  IconEdit,
} from "@tabler/icons";
import BillReceipt from "./receipt";
import { getUser } from "../../helpers/auth";
import { usePaystackPayment } from "react-paystack";
import AddChargesComponent from "./add-charges";
import { BILL_STATE, PAYMENT_METHODS, isBillDateEditableForUser, MEMBERSHIP_BENEFIT_TYPE } from "../../helpers/config";
import { DatePicker } from "@mantine/dates";
import { RedeemBenefitsButton } from "./redeem-benefits-button";
import { RedeemedBenefitsDisplay } from "./redeemed-benefits-display";
import { RedeemBenefitsModal } from "./redeem-benefits-modal";
import { MembershipStatus } from "../shared";
import { CheckoutHandler } from "./checkout-handler";
import BillSummary from "./bill-summary";
import { BillAttachments } from "./bill-attachments";

const EditBillForm = ({ billId, onUpdate }) => {
  // states
  const [state, setState] = useState({});
  const [serverServices, setServerServices] = useState([]);
  const [items, setItems] = useState([]);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [showCharges, setShowCharges] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  // In-place editing of a published bill (does NOT convert the bill to a draft).
  const [isEditing, setIsEditing] = useState(false);
  const [showRedeemBenefitsModal, setShowRedeemBenefitsModal] = useState(false);
  const [serverAttachments, setServerAttachments] = useState([]);
  const [pendingAttachmentFiles, setPendingAttachmentFiles] = useState([]);
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);

  // atom
  const [payments, addPayment] = useAtom(billPaymentsAtom);
  const [, setPayments] = useAtom(setBillPayments);
  const [charges, addCharge] = useAtom(billChargesAtom);
  const [, setCharges] = useAtom(setBillCharges);
  const [, deleteCharge] = useAtom(deleteBillChargeAtom);
  const [, updateCharge] = useAtom(updateBillChargeAtom);
  const [redeemedBenefits, setRedeemedBenefits] = useAtom(
    billRedeemedBenefitsAtom
  );

  // queryClient for invalidating queries
  const queryClient = useQueryClient();

  // consts
  let subTotal = items?.reduce(
    (sum, { rate, quantity, discount = 0, discountType = "value" }) => {
      const itemSubtotal = parseFloat(rate) * parseInt(quantity);
      return sum + itemSubtotal;
    },
    0
  );

  // Calculate total item discounts
  let totalItemDiscounts = items?.reduce(
    (sum, { rate, quantity, discount = 0, discountType = "value" }) => {
      const itemSubtotal = parseFloat(rate) * parseInt(quantity);
      const itemDiscountAmount =
        discountType === "percent" ? (itemSubtotal * discount) / 100 : discount;
      return sum + itemDiscountAmount;
    },
    0
  );

  // Calculate total bill discount
  let totalBillDiscount = 0;
  if (state.discount > 0) {
    const billDiscountType = state.discountType || "value";
    totalBillDiscount =
      billDiscountType === "percent"
        ? (subTotal * state.discount) / 100
        : state.discount;
  }

  // Calculate total discount
  let totalDiscount = totalItemDiscounts + totalBillDiscount;

  let totalPayments = payments?.reduce(
    (sum, { amount }) => sum + parseFloat(amount),
    0
  );

  let totalCharges = charges?.reduce(
    (sum, { value }) => sum + parseFloat(value || 0),
    0
  );

  let totalDue = parseFloat(subTotal - (totalDiscount || 0) + totalCharges);
  let balance = parseFloat((totalDue || 0) - (totalPayments || 0));

  // queries
  const {
    data: patients,
    isFetching: fetchingPatients,
    refetch: refetchPatients,
  } = useQuery({
    queryFn: getAllNetworkPatients,
    queryKey: ["patients"],
  });

  const { isFetching: fetchingServices, refetch: refetchServices } = useQuery({
    queryFn: getAllServices,
    queryKey: ["services"],
    onSuccess: (data) => setServerServices(data),
  });

  const { data: serverCharges = [] } = useQuery({
    queryFn: () => getAllCharges(getUser().facility.id),
    queryKey: ["charges"],
    enabled: serverServices?.length > 0,
  });

  const {
    data: billData,
    isFetching,
    refetch,
  } = useQuery({
    queryFn: () => getBill(billId),
    queryKey: ["bill", billId],
    onSuccess: (data) => {
      setState({
        patientId: data.patient?.id,
        discount: data.discount || 0,
        discountType: data.discountType || "value",
        note: data.note || "",
        dueDate: data.dueDate,
        billDate: data.createdAt,
      });

      // set published state
      setIsPublished(data?.state === BILL_STATE.PUBLISHED);

      // Transform bill items to include discount fields and ensure proper data mapping
      const transformedItems =
        data.items?.map((item) => ({
          ...item,
          recordId: item.recordId || nanoid(),
          serviceId: item.serviceId || item.service?.id,
          serviceName: item.service?.serviceName || item.serviceName,
          rate: parseFloat(item.rate) || 0,
          quantity: parseInt(item.quantity) || 1,
          discount: parseFloat(item.discount) || 0,
          discountType: item.discountType || "value",
        })) || [];
      setItems(transformedItems);

      // Transform payments
      const transformedPayments =
        data.payments?.map((payment) => ({
          ...payment,
          recordId: nanoid(),
        })) || [];
      setPayments(transformedPayments);

      // Transform charges
      const transformedCharges =
        data?.charges?.map((chrg) => ({
          id: chrg?.id || nanoid(),
          chargeId: chrg?.charge?.id,
          value: chrg?.value,
          valueType: chrg?.valueType,
        })) || [];
      setCharges(transformedCharges);

      const transformedRedeemedBenefits = data?.redeemedBenefits || [];
      setRedeemedBenefits(transformedRedeemedBenefits);
      setServerAttachments(data?.attachments || []);
    },
  });

  // modals
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    size: "",
    zIndex: 55,
  });

  const [antModal, setAntModal] = useState({
    isOpen: false,
    title: "",
    content: null,
    width: "",
    zIndex: 60,
  });

  // Updates a bill in place (keeps its current published/draft state — no draft conversion).
  const { mutateAsync: updateBill, isLoading: isUpdating } = useMutation(
    (payload) => {
      const data = payload.data ?? payload;
      const files = payload.files ?? null;
      const attachmentsToKeep = payload.attachmentsToKeep;
      return putBill(data, files, attachmentsToKeep);
    },
    {
      onSuccess: (data, variables, context) => {
        if (data.status === 200) {
          message.success(data.data.message);
          setIsEditing(false);
          // Refresh all relevant data
          refetch(); // Refresh current bill data
          refetchPatients(); // Refresh patients data
          refetchServices(); // Refresh services data
          queryClient.invalidateQueries(["bills"]); // Refresh bills list
          queryClient.invalidateQueries(["member-benefits"]); // Refresh membership benefits
          onUpdate(); // Update parent component
          return;
        }
        throw data;
      },
      onError: (error, variables, context) => {
        const err = error?.response?.data?.message;
        if (_.isArray(err)) {
          err.map((err) => message.error(err.message));
        } else {
          message.error(err || "error processing request!");
        }
      },
    }
  );

  // handlers
  // const handleUpdateItem = (params) => {
  //   const { recordId, field, value } = params;
  //   const updatedItems = items.map((item) => {
  //     if (item.recordId === recordId) {
  //       return { ...item, [field]: value };
  //     }
  //     return item;
  //   });
  //   setItems(updatedItems);
  // };

  const handleUpdateItem = (params) => {
    let { recordId, field, value } = params;

    let item = items.find((pr) => pr.recordId === recordId);
    if (!item) return;

    if (field === "quantity" && value < 0) value = 0;

    if (field === "serviceId" && items.find((itm) => itm.serviceId === value)) {
      message.error("already selected!");
      return;
    }

    if (field === "serviceId") {
      item.rate = serverServices.find((s) => s.id === value)?.baseRate ?? item.rate;
    }
    item[field] = value;

    setItems(
      items.map((itm) => (itm.recordId === recordId ? item : itm))
    );
  };

  const handleDeleteItem = (recordId) => {
    setItems(items.filter((item) => item.recordId !== recordId));
  };

  const handleAdditem = () => {
    setItems([
      ...items,
      {
        recordId: nanoid(),
        serviceId: null,
        rate: 0,
        quantity: 1,
        discount: 0,
        discountType: "value",
      },
    ]);
  };

  // Prepare bill data for checkout
  const canEditBillDate = isBillDateEditableForUser(getUser());

  // Fields are editable for drafts always, and for published bills only after the
  // user clicks "Edit Bill" (which enables in-place editing without draft conversion).
  const editable = !isPublished || isEditing;

  const prepareBillData = () => {
    return {
      bill: {
        id: billId,
        patientId: state.patientId,
        discount: state.discount || 0,
        discountType: state.discountType || "value",
        note: state.note || "",
        dueDate: state.dueDate,
        ...(canEditBillDate && {
          billDate: state.billDate ? new Date(state.billDate) : new Date(),
        }),
        totalItemDiscounts: totalItemDiscounts,
        totalBillDiscount: totalBillDiscount,
        totalDiscount: totalDiscount,
        redeemedBenefits: redeemedBenefits,
        membershipId:
          patients?.find((pa) => pa.id === state.patientId)
            ?.membershipSubscription?.membershipId || null,
      },
      items: items.map((item) => ({
        serviceId: item.serviceId,
        rate: item.rate,
        quantity: item.quantity,
        discount: item.discount || 0,
        discountType: item.discountType || "value",
      })),
      payments: payments.map((payment) => ({
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
      })),
      charges: charges.map((charge) => ({
        chargeId: charge.chargeId,
        value: charge.value,
      })),
      redeemedBenefits: redeemedBenefits,
      totalItemDiscounts,
      totalBillDiscount,
      totalDiscount,
    };
  };

  // Checkout handler for edit mode
  const {
    handleCheckoutClick,
    isLoading: isCheckoutLoading,
    CheckoutConfirmationModal,
  } = CheckoutHandler({
    billData: prepareBillData(),
    totalDue,
    totalPayments,
    balance,
    isEditMode: true,
    isDraft: !isPublished,
    attachmentFiles: pendingAttachmentFiles,
    attachmentsToKeep: serverAttachments.map((a) => ({ fileUrl: a.fileUrl })),
    onSuccess: (data) => {
      // Refresh all relevant data
      refetch(); // Refresh current bill data
      refetchPatients(); // Refresh patients data
      refetchServices(); // Refresh services data
      queryClient.invalidateQueries(["bills"]); // Refresh bills list
      queryClient.invalidateQueries(["member-benefits"]); // Refresh membership benefits
      onUpdate(); // Update parent component

      // Show receipt modal
      setModal({
        isOpen: true,
        title: "Receipt",
        size: "md",
        content: <BillReceipt query={billId} param="billId" />,
        zIndex: 100,
      });
    },
    onError: (error) => {
      // Error handling is done in the CheckoutHandler component
    },
  });

  // Convert-to-draft disabled: published bills are edited in place and stay published.
  /*
  const convertToDraftMutation = useMutation({
    mutationFn: () => putConvertBillToDraft(billId),
    onSuccess: (response) => {
      if (response.status === 200) {
        message.success("Bill converted to draft successfully");
        setIsPublished(false);
        // Refresh all relevant data
        refetch(); // Refresh current bill data
        refetchPatients(); // Refresh patients data
        refetchServices(); // Refresh services data
        queryClient.invalidateQueries(["bills"]); // Refresh bills list
        queryClient.invalidateQueries(["member-benefits"]); // Refresh membership benefits
        onUpdate(); // Update parent component
      }
    },
    onError: (error) => {
      const err = error?.response?.data?.message;
      message.error(err || "Failed to convert bill to draft");
    },
  });

  const handleConvertToDraft = () => {
    setModal({
      isOpen: true,
      title: "Convert to Draft",
      content: (
        <div className="p-3">
          <div className="mb-3">
            <Text size="sm" weight={600} className="mb-2">
              Convert Published Bill to Draft?
            </Text>
            <Text size="xs" color="dimmed">
              This will convert the published bill back to draft status,
              allowing you to make changes. The bill will no longer be visible
              to the patient until published again.
            </Text>
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setModal({ isOpen: false })}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="orange"
              loading={convertToDraftMutation.isLoading}
              onClick={() => {
                setModal({ isOpen: false });
                convertToDraftMutation.mutate();
              }}>
              Convert to Draft
            </Button>
          </div>
        </div>
      ),
      size: "sm",
    });
  };
  */

  // Saves edits in place. For a published bill this updates it and keeps it
  // published (no draft conversion); for a draft it updates the draft.
  const handleSubmit = () => {
    if (_.isEmpty(state.patientId)) {
      return message.error("select a member");
    }

    if (items.length < 1) {
      return message.error("add at least one item");
    }

    if (!isPublished && redeemedBenefits.length > 0) {
      return message.error(
        "Care value cannot be used during draft saving. Please remove care value usage before saving as draft."
      );
    }

    if (
      !isPublished &&
      payments.some((p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT)
    ) {
      return message.error(
        "Care value usage cannot be added during draft saving. Please remove care value usage before saving as draft."
      );
    }

    const updateData = {
      bill: {
        id: billId,
        patientId: state.patientId,
        discount: state.discount || 0,
        discountType: state.discountType || "value",
        note: state.note || "",
        dueDate: state.dueDate,
        ...(canEditBillDate && {
          billDate: state.billDate ? new Date(state.billDate) : new Date(),
        }),
        totalItemDiscounts: totalItemDiscounts,
        totalBillDiscount: totalBillDiscount,
        totalDiscount: totalDiscount,
        redeemedBenefits: redeemedBenefits,
        membershipId:
          patients?.find((pa) => pa.id === state.patientId)
            ?.membershipSubscription?.membershipId || null,
      },
      items: items.map((item) => ({
        serviceId: item.serviceId,
        rate: item.rate,
        quantity: item.quantity,
        discount: item.discount || 0,
        discountType: item.discountType || "value",
      })),
      payments: payments.map((payment) => ({
        paymentMethod: payment.paymentMethod,
        amount: payment.amount,
      })),
      charges: charges.map((charge) => ({
        chargeId: charge.chargeId,
        value: charge.value,
      })),
    };

    // Show confirmation dialog
    setModal({
      isOpen: true,
      title: "Confirm Bill Update",
      content: (
        <div className="p-3">
          <div className="mb-3">
            <Text size="sm" weight={600} className="mb-2">
              Are you sure you want to update this bill?
            </Text>
            <Text size="xs" color="dimmed">
              This action will save all changes including items, discounts, and
              payments.
            </Text>
          </div>

          <div className="mb-3 p-2 bg-light rounded">
            <Text size="xs" weight={600} className="mb-1">
              Summary:
            </Text>
            <div className="d-flex justify-content-between mb-1">
              <Text size="xs">Items:</Text>
              <Text size="xs" weight={500}>
                {items.length}
              </Text>
            </div>
            <div className="d-flex justify-content-between mb-1">
              <Text size="xs">Subtotal:</Text>
              <Text size="xs" weight={500}>
                {cedisLocale.format(subTotal)}
              </Text>
            </div>
            {state.discount > 0 && (
              <div className="d-flex justify-content-between mb-1">
                <Text size="xs">Discount:</Text>
                <Text size="xs" weight={500} color="green">
                  -{cedisLocale.format(state.discount)}
                </Text>
              </div>
            )}
            <div className="d-flex justify-content-between">
              <Text size="xs" weight={600}>
                Total Due:
              </Text>
              <Text size="xs" weight={600}>
                {cedisLocale.format(totalDue)}
              </Text>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setModal({ ...modal, isOpen: false })}>
              Cancel
            </Button>
            <Button
              color="blue"
              size="sm"
              loading={isUpdating}
              onClick={() => {
                setModal({ ...modal, isOpen: false });
                updateBill({
                  data: updateData,
                  files: pendingAttachmentFiles,
                  attachmentsToKeep: serverAttachments.map((a) => ({ fileUrl: a.fileUrl })),
                });
              }}>
              Confirm Update
            </Button>
          </div>
        </div>
      ),
      size: "md",
    });
  };

  const initializePayment = usePaystackPayment(
    configurePaystack(
      calculatePaystackAmount(payments),
      patients?.find((pa) => pa.id === state.patientId),
      {
        userId: getUser().id,
        facility: getUser().facility?.facilityName,
        branch: getUser().branches?.find((br) => br.id === getUser()?.branchId)
          ?.branchName,
        username: getUser().staff?.name,
      }
    )
  );

  const handleAddNote = () => {
    setModal({
      isOpen: true,
      title: editable ? "Add Bill Notes" : "Bill Notes",
      content: (
        <NotesModal
          note={state.note}
          readOnly={!editable}
          onSave={(note) => {
            setState({ ...state, note });
            setModal({ ...modal, isOpen: false });
            message.success("Notes updated successfully!");
          }}
          onCancel={() => setModal({ ...modal, isOpen: false })}
        />
      ),
      size: "md",
    });
  };

  const handleAddDiscount = () => {
    setModal({
      isOpen: true,
      title: editable ? "Add Bill Discount" : "Bill Discount",
      content: (
        <DiscountModal
          discount={state.discount}
          discountType={state.discountType}
          subTotal={subTotal}
          readOnly={!editable}
          onSave={({ discount, discountType }) => {
            setState({ ...state, discount, discountType });
            setModal({ ...modal, isOpen: false });
            message.success("Discount updated successfully!");
          }}
          onCancel={() => setModal({ ...modal, isOpen: false })}
        />
      ),
      size: "md",
    });
  };

  // Redeemed Benefits Handlers
  const handleBenefitsRedemption = async (selectedBenefits) => {
    // Calculate total redeemed amount
    const totalRedeemedAmount = selectedBenefits.reduce((total, benefit) => {
      return total + parseFloat(benefit.amount || 0);
    }, 0);

    // Validate that redeemed benefits don't exceed total bill amount
    if (totalRedeemedAmount > totalDue) {
      message.error(
        `Benefits of ${cedisLocale.format(
          totalRedeemedAmount
        )} exceeds total bill of ${cedisLocale.format(totalDue)}`
      );
      return;
    }

    // Merge new benefits with existing ones, combining duplicates by care category
    const mergedBenefits = [...redeemedBenefits];

    selectedBenefits.forEach((newBenefit) => {
      const existingBenefitIndex = mergedBenefits.findIndex(
        (existing) => existing.careCategoryId === newBenefit.careCategoryId
      );

      if (existingBenefitIndex >= 0) {
        // Update existing benefit amount
        mergedBenefits[existingBenefitIndex] = {
          ...mergedBenefits[existingBenefitIndex],
          amount:
            mergedBenefits[existingBenefitIndex].amount + newBenefit.amount,
        };
      } else {
        // Add new benefit
        mergedBenefits.push(newBenefit);
      }
    });

    setRedeemedBenefits(mergedBenefits);

    if (totalRedeemedAmount > 0) {
      const existingCareValuePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingCareValuePayment) {
        const updatedPayments = payments.map((payment) => {
          if (payment.recordId === existingCareValuePayment.recordId) {
            return {
              ...payment,
              amount: payment.amount + totalRedeemedAmount,
            };
          }
          return payment;
        });
        setPayments(updatedPayments);
      } else {
        const newPayment = {
          recordId: nanoid(),
          paymentMethod: PAYMENT_METHODS.HEALTH_CREDIT,
          amount: totalRedeemedAmount,
        };
        setPayments([...payments, newPayment]);
      }
    }

    // Close the modal after successful redemption
    setShowRedeemBenefitsModal(false);
  };

  const handleDeleteRedeemedBenefit = (benefitIndex) => {
    const benefitToDelete = redeemedBenefits[benefitIndex];
    const newRedeemedBenefits = redeemedBenefits.filter(
      (_, index) => index !== benefitIndex
    );

    setRedeemedBenefits(newRedeemedBenefits);

    if (benefitToDelete.amount > 0) {
      const existingCareValuePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingCareValuePayment) {
        const newAmount =
          existingCareValuePayment.amount - benefitToDelete.amount;

        if (newAmount <= 0) {
          const updatedPayments = payments.filter(
            (p) => p.recordId !== existingCareValuePayment.recordId
          );
          setPayments(updatedPayments);
        } else {
          const updatedPayments = payments.map((payment) => {
            if (payment.recordId === existingCareValuePayment.recordId) {
              return {
                ...payment,
                amount: newAmount,
              };
            }
            return payment;
          });
          setPayments(updatedPayments);
        }
      }
    }
  };

  const handleClearAllRedeemedBenefits = () => {
    const totalRedeemedAmount = redeemedBenefits.reduce(
      (sum, benefit) => sum + benefit.amount,
      0
    );

    setRedeemedBenefits([]);

    if (totalRedeemedAmount > 0) {
      const existingCareValuePayment = payments.find(
        (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
      );

      if (existingCareValuePayment) {
        const updatedPayments = payments.filter(
          (p) => p.recordId !== existingCareValuePayment.recordId
        );
        setPayments(updatedPayments);
      }
    }
  };

  // Care value cannot be used while a bill is still a draft; used to gate draft saving.
  const hasCareValueUsage =
    redeemedBenefits.length > 0 ||
    payments.some((p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT);

  const stepCircle = {
    width: 28,
    height: 28,
    minWidth: 28,
    backgroundColor: "#0991cc",
    color: "#fff",
    fontSize: "0.85rem",
    fontWeight: 700,
  };

  return (
    <div className="row g-4">
      <Modal
        onClose={() => setModal({ ...modal, isOpen: false })}
        opened={modal.isOpen}
        title={modal.title}
        size={modal.size || "md"}
        zIndex={55}>
        {modal.content}
      </Modal>
      <AntModal
        open={antModal.isOpen}
        onCancel={() => setAntModal({ ...antModal, isOpen: false })}
        title={antModal.title}
        width={antModal.width}
        zIndex={60}
        footer={false}>
        {antModal.content}
      </AntModal>

      {/* Redeem Benefits Modal */}
      <RedeemBenefitsModal
        isOpen={showRedeemBenefitsModal}
        onClose={() => setShowRedeemBenefitsModal(false)}
        membershipId={
          patients?.find((pa) => pa.id === state.patientId)
            ?.membershipSubscription?.membershipId
        }
        totalBillAmount={totalDue}
        onRedeem={handleBenefitsRedemption}
      />

      {/* Checkout Confirmation Modal */}
      {CheckoutConfirmationModal}

      <div className="col-12">
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle me-3"
              style={{
                width: 44,
                height: 44,
                backgroundColor: "rgba(9, 145, 204, 0.12)",
              }}>
              <IconReceipt size={24} color="#0991cc" />
            </div>
            <div>
              <Text size="xl" weight={700} style={{ lineHeight: 1.1 }}>
                {isPublished ? "Edit Bill" : "Edit Draft"}
              </Text>
              <Text size="sm" color="dimmed">
                {isPublished
                  ? "Review or update this published bill."
                  : "Update, save, or check out this draft."}
              </Text>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {billData?.billNumber && (
              <Badge color="gray" variant="light" size="lg">
                #{billData.billNumber}
              </Badge>
            )}
            <Badge
              color={isPublished ? "green" : "yellow"}
              className={isPublished ? "text-white" : "text-black"}
              size="lg"
              variant="filled">
              {isPublished ? "Published" : "Draft"}
            </Badge>
          </div>
        </div>
      </div>

      {
        <div className="col-lg-8 col-12">
          <div className="border rounded-3 bg-white p-4 mb-4">
            <div className="d-flex align-items-center mb-3">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                style={stepCircle}>
                1
              </span>
              <div>
                <Text weight={600}>Member &amp; bill date</Text>
                <Text size="xs" color="dimmed">
                  Who is this bill for?
                </Text>
              </div>
            </div>
            {isFetching ? (
              <Skeleton height={100} />
            ) : (
              <div className="row align-items-start">
                <div className={canEditBillDate ? "col-md-8" : "col-12"}>
                  <Text size="sm" weight={600} className="mb-2 d-block">
                    Member
                  </Text>
                  <Select
                    value={state.patientId}
                    onChange={(value) =>
                      setState({ ...state, patientId: value })
                    }
                    disabled={!editable}
                    placeholder="Select patient"
                    data={
                      patients?.map((pa) => {
                        const hasCashlessCareValue =
                          pa.membershipSubscription?.id &&
                          pa.membershipSubscription?.benefitType !==
                          MEMBERSHIP_BENEFIT_TYPE.DISCOUNTED &&
                          pa.membershipSubscription?.benefitType !==
                          MEMBERSHIP_BENEFIT_TYPE.REGULAR;
                        return {
                          label: `${pa.firstName} ${pa.lastName} (${pa.contact || ''})${hasCashlessCareValue ? " ✔️" : ""}`,
                          value: pa.id,
                        };
                      }) || []
                    }
                    searchable
                    clearable
                    size="md"
                  />
                </div>

                {canEditBillDate && (
                  <div className="col-md-4 mt-3 mt-md-0">
                    <Text size="sm" weight={600} className="mb-2 d-block">
                      Bill date
                    </Text>
                    <DatePicker
                      value={
                        state.billDate
                          ? new Date(state.billDate)
                          : new Date()
                      }
                      onChange={(d) =>
                        setState({
                          ...state,
                          billDate: d ? d.toISOString() : null,
                        })
                      }
                      disabled={!editable}
                      size="md"
                      maxDate={new Date()}
                    />
                  </div>
                )}

                <div className="col-12">
                  {!isPublished && (
                    <RedeemBenefitsButton
                      hasMembership={
                        patients?.find((pa) => pa.id === state.patientId)
                          ?.membershipSubscription
                      }
                      patientName={
                        patients?.find((pa) => pa.id === state.patientId)
                          ?.firstName
                      }
                      subTotal={subTotal}
                      onRedeemClick={() => setShowRedeemBenefitsModal(true)}
                      disabled={isPublished}
                      showWarning={false}
                      benefitType={
                        patients?.find((pa) => pa.id === state.patientId)
                          ?.membershipSubscription?.benefitType
                      }
                      subscriptionId={
                        patients?.find((pa) => pa.id === state.patientId)
                          ?.membershipSubscription?.id
                      }
                      onViewPlan={(subscriptionId) =>
                        setModal({
                          isOpen: true,
                          title: "Membership Status",
                          content: (
                            <MembershipStatus membershipId={subscriptionId} />
                          ),
                          size: 1200,
                        })
                      }
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Bill Items Section */}
          <div className="border rounded-3 p-4 bg-white mb-4">
            {isFetching ? (
              <Skeleton height={100} />
            ) : (
              <>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center">
                    <span
                      className="d-inline-flex align-items-center justify-content-center rounded-circle me-3"
                      style={stepCircle}>
                      2
                    </span>
                    <div>
                      <Text weight={600}>Bill items</Text>
                      <Text size="xs" color="dimmed">
                        Services included on this bill.
                      </Text>
                    </div>
                  </div>
                  <button
                    onClick={handleAdditem}
                    disabled={!editable}
                    className={`button is-small is-success fw-bold ${fetchingServices ? "is-loading" : ""
                      }`}>
                    <span className="bi bi-plus-lg me-1" />
                    Add item
                  </button>
                </div>

                {items?.length > 0 ? (
                  <>
                    <div className="row fw-bold text-muted small mb-2 px-1">
                      <div className="col-4">Service</div>
                      <div className="col-2 text-center">Price</div>
                      <div className="col-2">Qty</div>
                      <div className="col-2">Discount</div>
                      <div className="col-1 text-end">Total</div>
                      <div className="col-1"></div>
                    </div>
                    <div className="border rounded">
                      {items.map((item, i) => (
                        <LineItemsComponent
                          key={item.recordId}
                          item={item}
                          services={serverServices}
                          onUpdate={handleUpdateItem}
                          onDelete={handleDeleteItem}
                          canEditPrice={canEditBillDate}
                          disabled={!editable}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  <div
                    className="text-center py-5 text-muted rounded-3"
                    style={{ border: "1px dashed #d0d5dd" }}>
                    <IconReceipt size={40} className="mb-2" />
                    <Text size="sm" className={editable ? "mb-3" : ""}>
                      No items in bill
                    </Text>
                    {editable && (
                      <button
                        onClick={handleAdditem}
                        disabled={!editable}
                        className={`button is-small is-success fw-bold ${fetchingServices ? "is-loading" : ""
                          }`}>
                        <span className="bi bi-plus-lg me-1" />
                        Add your first item
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>



          {/* Payments Section - Hidden by default with toggle */}
          {showPaymentMethods && (
            <div className="rounded border p-3 mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <IconCreditCard size={20} className="me-2" />
                  <Text weight={600}>Payments</Text>
                </div>
              </div>
              <EditPayments readOnly={!editable} />
            </div>
          )}

          {/* Charges Section - Hidden by default with toggle */}
          {showCharges && (
            <div className="rounded border p-3 mb-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <IconReceipt size={20} className="me-2" />
                  <Text weight={600}>Charges</Text>
                </div>
              </div>
              <EditCharges
                readOnly={!editable}
                allCharges={serverCharges}
                charges={charges}
                onAddCharge={addCharge}
                onDeleteCharge={deleteCharge}
                onUpdateCharge={(updatedCharge) => {
                  // Handle the update based on the structure expected by the atom
                  if (updatedCharge.chargeId !== undefined) {
                    updateCharge({
                      id: updatedCharge.id,
                      field: "chargeId",
                      value: updatedCharge.chargeId,
                    });
                  }
                  if (updatedCharge.value !== undefined) {
                    updateCharge({
                      id: updatedCharge.id,
                      field: "value",
                      value: updatedCharge.value,
                    });
                  }
                }}
                billSubTotalBeforeCharges={
                  subTotal - totalItemDiscounts - totalBillDiscount
                }
              />
            </div>
          )}
        </div>
      }

      <div className="col-lg-4 col-12">
        <div className="position-sticky" style={{ top: "1rem" }}>
          <div className="border rounded-3 bg-white p-4">
            <div className="d-flex align-items-center mb-2">
              <IconCreditCard size={20} className="me-2" />
              <Text weight={600}>Bill summary</Text>
            </div>

          {state.note && (
            <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200">
              <div className="d-flex align-items-center mb-2">
                <IconNotes size={14} className="me-1" />
                <Text size="xs" weight={600} color="blue">
                  Bill Notes
                </Text>
              </div>
              <Text size="xs" className="text-muted">
                {state.note}
              </Text>
            </div>
          )}

          {/* Bill Discount Details */}
          {state.discount > 0 && (
            <div className="mb-3 p-3 bg-green-50 rounded border border-green-200">
              <div className="d-flex align-items-center mb-2">
                <IconDiscount size={14} className="me-1" />
                <Text size="xs" weight={600} color="green">
                  Bill Discount
                </Text>
              </div>
              <Text size="xs" color="green" weight={500}>
                {totalBillDiscount > 0
                  ? cedisLocale.format(totalBillDiscount)
                  : cedisLocale.format(state.discount)}{" "}
                (
                {state.discountType === "percent"
                  ? `${state.discount}%`
                  : "Fixed Amount"}
                )
              </Text>
            </div>
          )}

          <BillSummary
            subTotal={subTotal}
            totalCharges={totalCharges}
            totalDiscount={totalDiscount}
            totalDue={totalDue}
            totalPayments={totalPayments}
            balance={balance}
            totalItemDiscounts={totalItemDiscounts}
            totalBillDiscount={totalBillDiscount}
            billDiscount={state.discount}
            billDiscountType={state.discountType}
            redeemedBenefits={redeemedBenefits}
            onDeleteBenefit={handleDeleteRedeemedBenefit}
            onClearAllBenefits={handleClearAllRedeemedBenefits}
            onAddNote={handleAddNote}
            onAddDiscount={handleAddDiscount}
            onAddCharges={() => setShowCharges(!showCharges)}
            hasNote={!!state.note}
            hasDiscount={totalDiscount > 0}
            hasCharges={totalCharges > 0}
            isDraft={!isPublished}
            draftBillId={null}
          />


          <Divider className="my-3" />

          <Text
            size="xs"
            weight={700}
            color="dimmed"
            className="text-uppercase mb-2 d-block"
            style={{ letterSpacing: "0.04em" }}>
            Adjustments
          </Text>
          <div className="row g-2 mb-3">
            <div className="col-6">
              <button
                title={editable ? "Bill notes" : "View bill notes"}
                onClick={handleAddNote}
                className={`button w-100 d-flex align-items-center justify-content-center ${!!state.note ? "is-success is-light" : ""
                  }`}>
                <span className="bi bi-file-earmark-text me-2" />
                Notes
              </button>
            </div>
            <div className="col-6">
              <button
                title={editable ? "Bill discount" : "View bill discount"}
                onClick={handleAddDiscount}
                className={`button w-100 d-flex align-items-center justify-content-center ${totalDiscount > 0 ? "is-success is-light" : ""
                  }`}>
                <span className="bi bi-percent me-2" />
                Discount
              </button>
            </div>
            <div className="col-6">
              <button
                title={editable ? "Bill payments" : "View bill payments"}
                onClick={() => setShowPaymentMethods(!showPaymentMethods)}
                className={`button w-100 d-flex align-items-center justify-content-center ${showPaymentMethods ? "is-info is-light border-info" : ""
                  }`}>
                <span className="bi bi-cash-coin me-2" />
                Payments
              </button>
            </div>
            <div className="col-6">
              <button
                title={editable ? "Bill charges" : "View bill charges"}
                onClick={() => setShowCharges(!showCharges)}
                className={`button w-100 d-flex align-items-center justify-content-center ${showCharges ? "is-info is-light border-info" : ""
                  }`}>
                <span className="bi bi-plus-circle me-2" />
                Charges
              </button>
            </div>
          </div>

          {!isPublished && (
            <>
              <button
                onClick={handleCheckoutClick}
                className={`button h6 py-4 w-100 app-btn btn-prim text-white d-flex justify-content-center align-items-center mb-2 ${isCheckoutLoading && " is-loading"
                  }`}>
                <span className="bi bi-check-circle me-2" />
                <span className="fw-bold">CHECK OUT</span>
              </button>

              <button
                onClick={handleSubmit}
                disabled={hasCareValueUsage || isUpdating}
                title={
                  redeemedBenefits.length > 0
                    ? "Remove care value usage to enable saving"
                    : payments.some(
                      (p) => p.paymentMethod === PAYMENT_METHODS.HEALTH_CREDIT
                    )
                      ? "Remove care value payment method to enable saving"
                      : "Save"
                }
                className={`button h6 py-4 w-100 app-btn d-flex justify-content-center align-items-center mb-2 ${hasCareValueUsage ? "bg-light text-muted" : "bg-warning text-black"
                  } ${isUpdating ? " is-loading" : ""}`}>
                <span className="bi bi-save me-2" />
                SAVE
                {hasCareValueUsage && (
                  <span className="bi bi-exclamation-triangle ms-2 text-warning" />
                )}
              </button>
            </>
          )}

          {isPublished && !isEditing && (
            <>
              <button
                className="button h6 py-4 w-100 app-btn bg-warning text-black d-flex justify-content-center align-items-center mb-2"
                onClick={() => setIsEditing(true)}>
                <IconEdit size={16} className="me-2" />
                Edit Bill
              </button>

              <button
                className="button h6 py-4 w-100 btn-prim d-flex justify-content-center align-items-center mb-2"
                style={{ color: "#000" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#000")}
                onClick={() => {
                  setModal({
                    isOpen: true,
                    title: "Receipt",
                    size: "md",
                    content: <BillReceipt query={billId} param="billId" />,
                    zIndex: 100,
                  });
                }}>
                <IconPrinter size={16} />
                <span className="ms-2">Print Receipt</span>
              </button>
            </>
          )}

          {isPublished && isEditing && (
            <>
              <button
                onClick={handleSubmit}
                disabled={isUpdating}
                className={`button h6 py-4 w-100 app-btn btn-prim text-white d-flex justify-content-center align-items-center mb-2 ${isUpdating ? " is-loading" : ""
                  }`}>
                <span className="bi bi-check-circle me-2" />
                Save Changes
              </button>

              <button
                className="button h6 py-4 w-100 app-btn bg-warning text-black d-flex justify-content-center align-items-center mb-2"
                disabled={isUpdating}
                onClick={() => {
                  setIsEditing(false);
                  refetch(); // discard unsaved UI edits
                }}>
                Cancel
              </button>
            </>
          )}
          {!isPublished && (
            <button
              onClick={() => {
                setModal({
                  isOpen: true,
                  title: "Draft Receipt",
                  size: "md",
                  content: <BillReceipt query={billId} param="billId" />,
                  zIndex: 100,
                });
              }}
              className="button py-4 w-100 app-btn text-black d-flex justify-content-center align-items-center mb-2">
              <IconReceipt size={16} />
              <span className="ms-2 fw-bold">Preview Receipt</span>
            </button>
          )}
          <button
            type="button"
            className="button is-primary is-light w-100"
            onClick={() => setAttachmentsModalOpen(true)}>
            <span className="bi bi-paperclip me-2" />
            {serverAttachments.length + pendingAttachmentFiles.length === 0
              ? "Add attachments"
              : `Edit attachments (${serverAttachments.length + pendingAttachmentFiles.length}/5)`}
          </button>
          <Modal
            opened={attachmentsModalOpen}
            onClose={() => setAttachmentsModalOpen(false)}
            title="Bill attachments"
            size="md"
            zIndex={55}>
            <BillAttachments
              attachments={serverAttachments}
              pendingFiles={pendingAttachmentFiles}
              onFilesChange={setPendingAttachmentFiles}
              onRemoveAttachment={(a) =>
                setServerAttachments((prev) =>
                  prev.filter((x) => x.id !== a.id)
                )
              }
            />
          </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBillForm;

// Discount Modal Component
const DiscountModal = ({
  discount = 0,
  discountType = "value",
  subTotal = 0,
  readOnly = false,
  onSave,
  onCancel,
}) => {
  const [localDiscount, setLocalDiscount] = useState(discount);
  const [localDiscountType, setLocalDiscountType] = useState(discountType);

  // Update local state when props change
  useEffect(() => {
    setLocalDiscount(discount);
    setLocalDiscountType(discountType);
  }, [discount, discountType]);

  const handleSave = () => {
    onSave({
      discount: localDiscount,
      discountType: localDiscountType,
    });
  };

  return (
    <div>
      <div className="mb-3">
        <Text size="xs" color="dimmed">
          {readOnly
            ? "Discount applied to the total bill amount."
            : "Add a discount to the total bill amount."}
        </Text>
      </div>

      <div className="field has-addons has-addons-centered">
        {/* <div className="d-flex gap-1">
            <Button
              variant={localDiscountType === "percent" ? "filled" : "outline"}
              color={localDiscountType === "percent" ? "blue" : "gray"}
              size="xs"
              onClick={() => setLocalDiscountType("percent")}>
              %
            </Button>
            <Button
              variant={localDiscountType === "value" ? "filled" : "outline"}
              color={localDiscountType === "value" ? "blue" : "gray"}
              size="xs"
              onClick={() => setLocalDiscountType("value")}>
              GHS
            </Button>
          </div>
        </p> */}
        <input
          className="input"
          type="number"
          step="0.01"
          autoFocus={!readOnly}
          disabled={readOnly}
          value={localDiscount || 0}
          placeholder={
            localDiscountType === "percent"
              ? "Input discount % (e.g 20)"
              : "Input discount value (e.g 50)"
          }
          onChange={(e) => {
            const value = parseFloat(e.target.value) || 0;
            setLocalDiscount(value);
          }}
        />
      </div>

      {localDiscount > 0 && (
        <div className="mb-3 p-2 bg-green-50 rounded border border-green-200">
          <Text size="xs" weight={600} color="green" className="mb-1">
            Discount Preview:
          </Text>
          <Text size="xs" color="green">
            {localDiscountType === "percent"
              ? `${localDiscount}% = ${cedisLocale.format(
                (subTotal * localDiscount) / 100
              )}`
              : `${cedisLocale.format(localDiscount)}`}
          </Text>
          <Text size="xs" color="green" className="mt-1">
            Final Total:{" "}
            {cedisLocale.format(
              localDiscountType === "percent"
                ? subTotal - (subTotal * localDiscount) / 100
                : subTotal - localDiscount
            )}
          </Text>
        </div>
      )}

      <div className="d-flex gap-2 justify-content-end mt-3">
        {readOnly ? (
          <Button variant="subtle" size="sm" onClick={onCancel}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="subtle" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button color="blue" size="sm" onClick={handleSave}>
              Save Discount
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

// Notes Modal Component
const NotesModal = ({ note = "", readOnly = false, onSave, onCancel }) => {
  const [localNote, setLocalNote] = useState(note);

  // Update local state when props change
  useEffect(() => {
    setLocalNote(note);
  }, [note]);

  const handleSave = () => {
    onSave(localNote);
  };

  return (
    <div>
      <div className="mb-3">
        <Text size="xs" color="dimmed">
          {readOnly
            ? "Notes on this bill."
            : "Add notes or comments to this bill."}
        </Text>
      </div>

      <textarea
        name="note"
        id="note"
        className="textarea w-100"
        cols="30"
        rows="8"
        disabled={readOnly}
        placeholder={
          readOnly ? "No notes on this bill." : "Start typing notes here..."
        }
        value={localNote}
        onChange={(e) => setLocalNote(e.target.value)}
      />

      <div className="d-flex gap-2 justify-content-end mt-3">
        {readOnly ? (
          <Button variant="subtle" size="sm" onClick={onCancel}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="subtle" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button color="blue" size="sm" onClick={handleSave}>
              Save Notes
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
